import {isNotNullOrUndefined} from '@benchling/js-utils';
import React from 'react';

import * as contextmenu from 'components/ContextMenu';
import {
  ActionFunctions,
  BaseActionFunctionParameters,
  LastSyncedUpstreamValue,
  LiveRowMetadata,
  SchemaFieldColumnUpdate,
  StatusFilter,
  StructuredTable,
  StructuredTableAdapter,
  TableActionFunctionParameters,
} from 'notebook/structured-tables/StructuredTableConstants';
import {OutsideNotebookContext} from 'notebook/structured-tables/StructuredTableOutsideNotebookConstants';
import {SelectionRange} from 'notebook/tables/constants/TableSelectionConstants';
import {TableIndicatorStatus} from 'notebook/tables/errors/TableErrorConstants';
import {EvaluatedCell} from 'notebook/tables/excess/Evaluator';
import {
  Cells,
  Table,
  TableHeaderControlsArgs,
  TableHeaderControlsCreator,
  TableOpFunction,
  TableOpOptions,
} from 'notebook/tables/TableConstants';
import {Note} from 'notebook/util/NoteUtilConstants';
import OverflowMenu from 'ui-library/menus/OverflowMenu';

import {ActionFunctionContext} from '../action-functions/ActionFunctionContext';

export interface GetActionFunctionsParams<TTableMetadata, TRowMetadata> {
  entryId: string | null;
  structuredTable: StructuredTable<TTableMetadata, TRowMetadata>;
  adapter: StructuredTableAdapter<TTableMetadata, TRowMetadata>;
  computedTable: Array<Array<EvaluatedCell>>;
  table?: Table;
  liveRowMetadataByRow?: Array<LiveRowMetadata>;
  tableStatus?: TableIndicatorStatus;
  locked: boolean;
  isProtected?: boolean;
  updateHandler: (params: {
    updatedCells: Cells;
    columnUpdates: {[columnId: number]: SchemaFieldColumnUpdate | {}};
    lastSyncedUpstreamValuesByRow?: Array<Array<LastSyncedUpstreamValue>>;
    lastSyncedArchivePurposeByRow?: Array<string | null | undefined>;
    isFinalizedByRow?: Array<boolean>;
    filters?: Array<StatusFilter>;
  }) => void;
  performTableOp: ({op, options}: {op: TableOpFunction; options?: TableOpOptions}) => void;
  note?: Note | null;
  outsideNotebookContext?: OutsideNotebookContext | null;
  actionFunctionContext: ActionFunctionContext;
  isFilterStale: boolean;
}

export default function getActionFunctions<TTableMetadata, TRowMetadata>(
  params: GetActionFunctionsParams<TTableMetadata, TRowMetadata>
): {
  rightHeaderControls: TableHeaderControlsCreator;
  contextMenuOptionGetters: {
    rowAxis: (selectionRange: SelectionRange | null) => Array<contextmenu.ContextMenuOption> | null;
  };
  footerControls?: JSX.Element | null;
} {
  const baseActionFunctionParams: BaseActionFunctionParameters<TTableMetadata, TRowMetadata> = {
    entryOrTemplateId: params.entryId,
    structuredTable: params.structuredTable,
    structuredTableAdapter: params.adapter,
    computedTable: params.computedTable,
    table: params.table,
    liveRowMetadataByRow: params.liveRowMetadataByRow ?? new Array(params.computedTable.length).fill({}),
    tableStatus: params.tableStatus ?? TableIndicatorStatus.NO_STATUS,
    updateHandler: params.updateHandler,
    locked: params.locked,
    isProtected: params.isProtected,
    performTableOpOnTable: params.performTableOp,
    note: params.note,
    outsideNotebookContext: params.outsideNotebookContext,
    actionFunctionContext: params.actionFunctionContext,
    isFilterStale: params.isFilterStale ?? false,
  };
  const actionFunctions = params.adapter.getActionFunctions(
    params.structuredTable.metadata,
    params.structuredTable.schemas
  );

  return {
    rightHeaderControls: getTableHeaderActionFunctions(actionFunctions, baseActionFunctionParams),
    contextMenuOptionGetters: {
      rowAxis: getRowContextMenuActionFunctions(actionFunctions, baseActionFunctionParams),
    },
    footerControls: getTableFooterActionFunctions(actionFunctions, baseActionFunctionParams),
  };
}

/**
 * This hook creates the action functions for the right side of a structured table's header. The
 * return value is meant to be passed directly to a TableEditable as its `headerControls` prop.
 */
function getTableHeaderActionFunctions<TTableMetadata, TRowMetadata>(
  actionFunctions: ActionFunctions<TTableMetadata, TRowMetadata>,
  baseParams: BaseActionFunctionParameters<TTableMetadata, TRowMetadata>
): TableHeaderControlsCreator {
  return ({multiSelectionBox, isCollapsed}: TableHeaderControlsArgs) => {
    const actionFunctionParams: TableActionFunctionParameters<TTableMetadata, TRowMetadata> = {
      ...baseParams,
      multiSelectionBox,
      isCollapsed,
    };
    // When collapsed, we only show the primary action function buttons if visible.
    if (isCollapsed) {
      if (actionFunctions.primary) {
        return (
          <>
            {actionFunctions.primary.map((actionFunction, index) => (
              <React.Fragment key={index}>
                {actionFunction.renderToolbarButton(actionFunctionParams)}
              </React.Fragment>
            ))}
          </>
        );
      }
      return null;
    }
    const overFlowOptions = (actionFunctions.overflow ?? [])
      .map((actionFunction) => actionFunction.getOverflowMenuItem(actionFunctionParams))
      .filter((option) => option != null);
    return (
      <>
        {actionFunctions.secondary?.map((actionFunction, index) => (
          <React.Fragment key={index}>
            {actionFunction.renderToolbarButton(actionFunctionParams)}
          </React.Fragment>
        ))}
        {overFlowOptions.length > 0 ? (
          <OverflowMenu options={overFlowOptions} menuProps={{dropLeft: true}} />
        ) : null}
        <>
          {actionFunctions.primary?.map((actionFunction, index) => (
            <React.Fragment key={index}>
              {actionFunction.renderToolbarButton(actionFunctionParams)}
            </React.Fragment>
          ))}
        </>
      </>
    );
  };
}

function getTableFooterActionFunctions<TTableMetadata, TRowMetadata>(
  actionFunctions: ActionFunctions<TTableMetadata, TRowMetadata>,
  baseParams: BaseActionFunctionParameters<TTableMetadata, TRowMetadata>
): JSX.Element | null {
  const actionFunctionParams = {...baseParams, multiSelectionBox: null};
  return (
    <>
      {actionFunctions.footer?.map((actionFunction, index) => (
        <React.Fragment key={index}>
          {actionFunction.renderToolbarButton(actionFunctionParams)}
        </React.Fragment>
      ))}
    </>
  );
}

/**
 * This hook creates action functions for a structured table row's context menu. The return value can be used
 * as TableAxisCell's getExtraContextMenuOptions prop.
 */
function getRowContextMenuActionFunctions<TTableMetadata, TRowMetadata>(
  actionFunctions: ActionFunctions<TTableMetadata, TRowMetadata>,
  baseParams: BaseActionFunctionParameters<TTableMetadata, TRowMetadata>
): (selectionRange: SelectionRange | null) => Array<contextmenu.ContextMenuOption> | null {
  const rowContextMenuActionFunctions =
    actionFunctions.contextMenu?.filter(
      (actionFunction) => actionFunction.getRowAxisContextMenuItem != null
    ) ?? [];

  return (selectionRange: SelectionRange | null) =>
    rowContextMenuActionFunctions
      .map(({getRowAxisContextMenuItem}) => getRowAxisContextMenuItem({...baseParams, selectionRange}))
      .filter((item) => isNotNullOrUndefined(item))
      .map((item) => ({name: item.name, click: item.onSelect}));
}
